import type { ReactNode } from 'react';
import type { TaggedInterventionGroup } from './types';
import type { MoveHoverParams } from './GanttCell';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import ChevronDoubleIcon from '../../assets/icons/chevron-double.svg?react';
import CheckIcon from '../../assets/icons/check.svg?react';
import styles from './GanttCell.module.css';

interface MoveButtonsProps {
  group: TaggedInterventionGroup;
  ivBlock: ReactNode;
  hasLeftNeighbor: boolean;
  hasRightNeighbor: boolean;
  seriesAtLeftEdge: boolean;
  seriesAtRightEdge: boolean;
  totalWeeks: number;
  moveWrapperRef: React.RefObject<HTMLDivElement | null>;
  onMoveHover?: (params: MoveHoverParams | null) => void;
  onMoveIntervention?: (params: MoveHoverParams) => void;
  onAccept: () => void;
}

export const MoveButtons = ({
  group,
  ivBlock,
  hasLeftNeighbor,
  hasRightNeighbor,
  seriesAtLeftEdge,
  seriesAtRightEdge,
  totalWeeks,
  moveWrapperRef,
  onMoveHover,
  onMoveIntervention,
  onAccept
}: MoveButtonsProps) => {
  const baseParams = {
    groupId: group.id ?? '',
    treatmentPlanId: group.treatmentPlanId,
    weekNumber: group.weekNumber
  };

  const canMoveLeft = group.weekNumber > 1;
  const canMoveRight = group.weekNumber < totalWeeks;

  return (
    <div
      ref={moveWrapperRef}
      className={styles.ivBlockMoveWrapper}
      data-move-wrapper
    >
      <div
        className={styles.moveButtonsLeft}
        onMouseLeave={() => onMoveHover?.(null)}
      >
        {!seriesAtLeftEdge && (
          <button
            type="button"
            className={styles.moveBtn}
            title="Move series left"
            onMouseEnter={() =>
              onMoveHover?.({ ...baseParams, scope: 'series', direction: 'left' })
            }
            onClick={() =>
              onMoveIntervention?.({ ...baseParams, scope: 'series', direction: 'left' })
            }
          >
            <ChevronDoubleIcon style={{ transform: 'rotate(180deg)' }} />
          </button>
        )}
        {!hasLeftNeighbor && canMoveLeft && (
          <button
            type="button"
            className={styles.moveBtn}
            title="Move 1 week left"
            onMouseEnter={() =>
              onMoveHover?.({ ...baseParams, scope: 'single', direction: 'left' })
            }
            onClick={() =>
              onMoveIntervention?.({ ...baseParams, scope: 'single', direction: 'left' })
            }
          >
            <ChevronIcon style={{ transform: 'rotate(180deg)' }} />
          </button>
        )}
      </div>
      {ivBlock}
      <div
        className={styles.moveButtonsRight}
        onMouseLeave={() => onMoveHover?.(null)}
      >
        {!hasRightNeighbor && canMoveRight && (
          <button
            type="button"
            className={styles.moveBtn}
            title="Move 1 week right"
            onMouseEnter={() =>
              onMoveHover?.({ ...baseParams, scope: 'single', direction: 'right' })
            }
            onClick={() =>
              onMoveIntervention?.({ ...baseParams, scope: 'single', direction: 'right' })
            }
          >
            <ChevronIcon />
          </button>
        )}
        {!seriesAtRightEdge && (
          <button
            type="button"
            className={styles.moveBtn}
            title="Move series right"
            onMouseEnter={() =>
              onMoveHover?.({ ...baseParams, scope: 'series', direction: 'right' })
            }
            onClick={() =>
              onMoveIntervention?.({ ...baseParams, scope: 'series', direction: 'right' })
            }
          >
            <ChevronDoubleIcon />
          </button>
        )}
        <button
          type="button"
          className={styles.moveBtn}
          title="Accept move"
          onClick={onAccept}
        >
          <CheckIcon />
        </button>
      </div>
    </div>
  );
};
